import { CourierDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { isCarrierProjectType } from '../../../../app/navigation/courier-project-type';
import { isCourierIncomplete } from '../../logic/courier-form.map';

export type CourierBreadcrumb = {
  label: string;
  /** Where the crumb links to. Absent renders plain text, for a level with no page to open. */
  url?: string;
};

/** Where the crumbs that have a page link to. Built by the caller, so this stays clear of the router. */
export type CourierBreadcrumbLinks = {
  carriers?: string;
  carrier?: string;
};

export type CourierBreadcrumbs = {
  /** Crumbs from the top of the hierarchy down to the courier's carrier. */
  breadcrumbs: CourierBreadcrumb[];
  /** The carrier's logo, shown beneath the breadcrumbs. */
  carrierImage?: string;
};

/**
 * `{organisation} > {carrier}`, so a region's courier reads "Test Carrier > Test Carrier Region 1".
 * Only `organisation` names the company; `project` is the administering one, which for a region
 * courier is the region itself. A carrier in no company falls back to that project, with a Carriers
 * level between unless the project is itself a carrier.
 *
 * Only the carrier and the Carriers level link, to whatever the caller passes — the company and the
 * project have no page every viewer can open.
 */
export function makeCourierBreadcrumbs(courier?: CourierDto, links?: CourierBreadcrumbLinks): CourierBreadcrumbs {
  if (!courier) {
    return { breadcrumbs: [] };
  }

  const { carrier, organisation, project } = courier;

  // An incomplete courier sits in a Phone Entered or Phone Verified group, not a carrier, so it has
  // no logo and no carrier page. The crumbs still say where it sits, as plain text; the logo
  // fallbacks would only put the network's logo on a courier no carrier has taken on yet.
  const incomplete = isCourierIncomplete(courier);
  const crumbLinks = incomplete ? undefined : links;

  // The logo lives on the carrier group; the company then the project are fallbacks.
  const carrierImage = incomplete ? undefined : carrier?.image || organisation?.imageURL || project?.imageURL;

  const carrierCrumb = { label: carrier?.name, url: crumbLinks?.carrier };
  const carriersCrumb = { label: 'Carriers', url: crumbLinks?.carriers };

  const trail = organisation
    ? [{ label: organisation.name }, carrierCrumb]
    : isCarrierProjectType(project?.projectType)
    ? [{ label: project?.name }, carrierCrumb]
    : [{ label: project?.name }, carriersCrumb, carrierCrumb];

  // A crumb whose data is absent is dropped rather than rendered as a gap, and a carrier project's
  // own top-level group carries the project's name, so a repeat collapses to one crumb. The later
  // one is kept, being the carrier with a page to link to.
  const crumbs = trail
    .filter((crumb): crumb is CourierBreadcrumb => !!crumb.label)
    .filter((crumb, index, all) => crumb.label !== all[index + 1]?.label);

  return { breadcrumbs: crumbs, carrierImage };
}
